import type {
  IDraftItem,
  IDraftSourceRef,
  IMenuDraft,
  IMenuImportPage,
} from '@/interfaces/IMenuImport';
import { listJoin } from '@/domain/store-setup/display-names';
import { allergenLabelKeys } from './item-editor';
import { hasLiveLine } from './live-update';
import { reviewQueue } from './menu-draft';
import type { TReviewQueueEntry } from './menu-draft';
import { checkReasonKey } from './workspace-view';

/**
 * Pure rules of «Revisar uno por uno» (plan §4.5.4): which entry comes next,
 * the progress, the review line of a dish and the crop of the menu photo
 * around it.
 */

/** Unique key of a queue entry. */
export function queueEntryKey(entry: TReviewQueueEntry): string {
  return `${entry.kind}:${entry.id}`;
}

export interface IQueueView {
  /** null: nothing left to review in this pass. */
  current: TReviewQueueEntry | null;
  /** «Revisar {index} de {total}». */
  index: number;
  total: number;
}

/**
 * The queue recomputed from the draft (open blocking questions, then red,
 * then amber dishes, in menu order) without the entries the owner already
 * answered, confirmed or skipped in this pass (`acted`). `saved` are dishes
 * the owner corrected: they count as reviewed once they left the queue.
 *
 * `pinnedKey` is the card on screen: it stays current while it is still in
 * the queue, even when a delta reorders the queue (the owner may be typing
 * in it). It gives way only to an action of the owner or when it leaves
 * the queue (fixed, removed, answered elsewhere).
 */
export function queueView(
  draft: IMenuDraft,
  acted: ReadonlySet<string>,
  saved: ReadonlySet<string>,
  pinnedKey: string | null = null
): IQueueView {
  const entries = reviewQueue(draft);
  const queued = new Set(entries.map(queueEntryKey));
  const remaining = entries.filter((entry) => !acted.has(queueEntryKey(entry)));
  let reviewed = acted.size;
  saved.forEach((key) => {
    if (!acted.has(key) && !queued.has(key)) reviewed++;
  });
  const pinned =
    pinnedKey === null
      ? undefined
      : remaining.find((entry) => queueEntryKey(entry) === pinnedKey);
  return {
    current: pinned ?? remaining[0] ?? null,
    index: reviewed + 1,
    total: reviewed + remaining.length,
  };
}

export interface IItemReviewLine {
  key: string;
  /** Filled into `{reason}` («Revise: el precio»); null when the key has no reason. */
  reasonKey: string | null;
  /** `allergens.*` keys joined into `{allergens}` («Revise los alérgenos: Gluten y Lácteos»); empty when the key has none. */
  allergenKeys: string[];
}

export interface IItemReviewText {
  tone: 'check' | 'fix';
  lines: IItemReviewLine[];
}

const CHECK_ALLERGENS_KEY = 'menuImport.item.checkAllergens';

function reviewLine(
  key: string,
  reasonKey: string | null = null,
  allergenKeys: string[] = []
): IItemReviewLine {
  return { key, reasonKey, allergenKeys };
}

/** The allergens read and not confirmed yet (`allergens.*` keys, in chip order); empty otherwise. */
export function unconfirmedAllergenKeys(
  item: Pick<IDraftItem, 'allergens' | 'allergensConfirmed'>
): string[] {
  return item.allergens !== 0 && !item.allergensConfirmed
    ? allergenLabelKeys(item.allergens)
    : [];
}

/**
 * The lines under a flagged dish: amber «Revise: …», red «Falta el precio»
 * / «Responda la pregunta de arriba». «Está bien» on an amber dish also
 * confirms its allergens, so allergens not confirmed yet are always named
 * («Revise los alérgenos: Gluten y Lácteos»): as the line itself when they
 * are the reason, else as a second line (review round 4, UX M1).
 *
 * Update sessions: an amber dish with a live line (a hold or a suggestion,
 * `hasLiveLine`) gets no generic line (the live line says why and how to
 * resolve it); a red one keeps its line under the live line. A dish already
 * on the menu never gets the allergens line (publishing never writes them).
 */
export function itemReviewText(
  item: Pick<
    IDraftItem,
    'review' | 'allergens' | 'allergensConfirmed' | 'flags' | 'live'
  >
): IItemReviewText | null {
  const { level, reason } = item.review;
  if (level === 'fix')
    return {
      tone: 'fix',
      lines: [
        reviewLine(
          reason === 'question'
            ? 'menuImport.item.answerQuestion'
            : 'menuImport.item.missingPrice'
        ),
      ],
    };
  if (level !== 'check' || hasLiveLine(item)) return null;
  const linked = !!item.live && item.live.dish !== null;
  const allergenKeys = linked ? [] : unconfirmedAllergenKeys(item);
  const allergensLine =
    allergenKeys.length > 0
      ? reviewLine(CHECK_ALLERGENS_KEY, null, allergenKeys)
      : null;
  if (reason === 'allergens' && allergensLine)
    return { tone: 'check', lines: [allergensLine] };
  const reasonKey = checkReasonKey(reason);
  const main =
    reasonKey === null
      ? reviewLine('menuImport.item.dot.check')
      : reviewLine('menuImport.item.check', reasonKey);
  return {
    tone: 'check',
    lines: allergensLine ? [main, allergensLine] : [main],
  };
}

/** Translates `key` with its params (`$t`). */
export type TReviewTranslate = (
  key: string,
  params?: Record<string, string>
) => string;

/** One review line as text: `{reason}` translated, `{allergens}` joined with `listJoin`. */
export function reviewLineText(
  line: IItemReviewLine,
  translate: TReviewTranslate,
  locale: string
): string {
  const params: Record<string, string> = {};
  if (line.reasonKey !== null) params.reason = translate(line.reasonKey);
  if (line.allergenKeys.length > 0)
    params.allergens = listJoin(
      line.allergenKeys.map((key) => translate(key)),
      locale
    );
  return translate(line.key, params);
}

// ---------------------------------------------------------------------------
// «Leímos esto:» — the menu photo around the dish
// ---------------------------------------------------------------------------

/** Room around the dish box, in 0–1000 page units. */
const CROP_PADDING = 30;
const BOX_SCALE = 1000;
/** Height ÷ width of the crop: never a sliver, never taller than 3:4. */
export const CROP_RATIO_MIN = 0.3;
export const CROP_RATIO_MAX = 0.75;
/** A whole page (no box) shows at most square, the page fitted inside. */
export const PAGE_RATIO_MAX = 1;

export interface ISourceCrop {
  /** The page image (`{uploadsPath}/{storageKey}`). */
  storageKey: string;
  /** Height ÷ width of the frame (`padding-top` in %). */
  ratio: number;
  /** CSS `background-size`. */
  size: string;
  /** CSS `background-position`. */
  position: string;
}

function clamp(value: number, low: number, high: number): number {
  return Math.min(high, Math.max(low, value));
}

function percent(value: number): string {
  return `${Math.round(value * 100000) / 1000}%`;
}

/** [a, b] grown (centred) to `size`, kept inside 0..1. */
function grow(a: number, b: number, size: number): [number, number] {
  const length = Math.min(1, size);
  const start = clamp((a + b) / 2 - length / 2, 0, 1 - length);
  return [start, start + length];
}

/**
 * The background that shows the page's rows y0..y1 in a frame `ratio` tall,
 * centred on x0..x1: exactly that part when the frame has its shape, the
 * whole width of it (and side bars) when the frame had to be capped.
 */
function backgroundFor(
  x0: number,
  x1: number,
  y0: number,
  y1: number,
  ratio: number,
  pageAspect: number
): Pick<ISourceCrop, 'size' | 'position'> {
  const h = y1 - y0;
  // Background width ÷ frame width (the frame width shows x1 - x0 when uncapped).
  const scale = (ratio / h) * pageAspect;
  const centre = (x0 + x1) / 2;
  const x = scale > 1 ? clamp((0.5 - centre * scale) / (1 - scale), 0, 1) : 0.5;
  const y = h >= 1 ? 0 : y0 / (1 - h);
  return {
    size: `${percent(scale)} ${percent(1 / h)}`,
    position: `${percent(x)} ${percent(y)}`,
  };
}

function wholePage(page: IMenuImportPage, storageKey: string): ISourceCrop {
  const ratio =
    page.width && page.height
      ? Math.min(page.height / page.width, PAGE_RATIO_MAX)
      : PAGE_RATIO_MAX;
  return { storageKey, ratio, size: 'contain', position: 'center' };
}

function cropAround(
  page: IMenuImportPage,
  storageKey: string,
  box: IDraftSourceRef['box']
): ISourceCrop {
  const width = page.width ?? 0;
  const height = page.height ?? 0;
  if (!box || width <= 0 || height <= 0) return wholePage(page, storageKey);
  const [ymin, xmin, ymax, xmax] = box.map((value) =>
    clamp(value, 0, BOX_SCALE)
  );
  if (ymax <= ymin || xmax <= xmin) return wholePage(page, storageKey);

  let x0 = Math.max(0, xmin - CROP_PADDING) / BOX_SCALE;
  let x1 = Math.min(BOX_SCALE, xmax + CROP_PADDING) / BOX_SCALE;
  let y0 = Math.max(0, ymin - CROP_PADDING) / BOX_SCALE;
  let y1 = Math.min(BOX_SCALE, ymax + CROP_PADDING) / BOX_SCALE;
  const ratioOf = () => ((y1 - y0) * height) / ((x1 - x0) * width);
  if (ratioOf() < CROP_RATIO_MIN)
    [y0, y1] = grow(y0, y1, (CROP_RATIO_MIN * (x1 - x0) * width) / height);
  else if (ratioOf() > CROP_RATIO_MAX)
    [x0, x1] = grow(x0, x1, ((y1 - y0) * height) / (CROP_RATIO_MAX * width));

  // A box as tall as a narrow page cannot widen enough: the frame is capped.
  const ratio = Math.min(ratioOf(), CROP_RATIO_MAX);
  return {
    storageKey,
    ratio,
    ...backgroundFor(x0, x1, y0, y1, ratio, width / height),
  };
}

/**
 * The first photo page the dish was read from, cropped around its box (the
 * whole page without a box); null for dishes from text or copied from
 * another store (nothing to show).
 */
export function sourceCrop(
  item: Pick<IDraftItem, 'sourceRefs'>,
  pages: IMenuImportPage[]
): ISourceCrop | null {
  for (const ref of item.sourceRefs) {
    const page = pages.find((candidate) => candidate.pageId === ref.pageId);
    if (page && page.kind === 'image' && page.storageKey)
      return cropAround(page, page.storageKey, ref.box);
  }
  return null;
}
